import React, {useCallback, useEffect, useMemo, useRef} from 'react';
import {DropdownV2, noop, PriorityIcon} from '@priority-software/priority-style-react';
import {priority} from '@priority-software/priority-proto-api/lib';
import usePortal from 'react-useportal';
import {FilterIconTrigger} from './parts/FilterIconTrigger';
import {iSearchDropdownCloseKey} from '../../../../_legacy/FieldSearch/utils/keys';
import {isRTL} from '../../../../Localization';
import {FilterDropdownContent} from './parts/FilterDropdownContent';
import {Column, apiLocker} from '../../../../stores/Form';
import {getDropdownDirections} from '../../utils/dropdownDirections';
import {showFormClientSideMessage} from '../../actions/messages';
import messagesText from '../../../../_shared/Messages/texts';
import {ConfirmProps} from '../../../../_shared/Messages/actions/MessageActions';

type directionElementProps = {
    x: number;
    y: number;
    width: number;
    height: number;
    top: number;
    right: number;
    bottom: number;
    left: number;
};

type FilterDropDownProps = {
    isOpen: boolean;
    onClose: (e?: Event) => void;
    column: Column;
    handleNavigationKeys: (e: KeyboardEvent) => void;
    dropdownStyle: Partial<directionElementProps>;
    getCellWrap: () => HTMLDivElement | null;
    onClickOutside: () => void;
};

export const FilterDropDown = ({
    isOpen,
    onClose,
    column,
    handleNavigationKeys,
    dropdownStyle,
    getCellWrap,
    onClickOutside
}: FilterDropDownProps) => {
    const {Portal} = usePortal();
    const isRTLDir = isRTL();
    const handleDropdownKeyDown = useCallback(
        event => {
            handleNavigationKeys(event);
        },
        [handleNavigationKeys]
    );
    const isChangedFilter = useRef<boolean>(false);
    //This flag needs to prevent open multiple instances of the client message on click outside method
    const isMessageShown = useRef<boolean>(false);

    const formSession = useMemo(() => column.parentForm.session, [column]);
    const onChangeFilter = useCallback(isChange => (isChangedFilter.current = isChange), []);

    const handleClickOutside = useCallback(() => {
        if (isChangedFilter.current) {
            if (!isMessageShown.current) {
                isMessageShown.current = true;

                const modalParams: Partial<ConfirmProps> = {
                    confirmButtonText: messagesText.yesExitSearch,
                    cancleButtonText: messagesText.cancel,
                    defaultButton: 0,
                    onConfirm: () => {
                        isMessageShown.current = false;
                        onClickOutside();
                    },
                    onClose: () => (isMessageShown.current = false),
                    title: messagesText.justMoment,
                    content: messagesText.searchStopsShouldExit
                };

                showFormClientSideMessage(
                    formSession,
                    priority.netitems.shared.Message.Type.Warning,
                    modalParams
                );
            }
        } else {
            onClickOutside();
        }
    }, [onClickOutside, formSession]);

    useEffect(() => {
        if (isOpen) {
            apiLocker.lockSession(formSession, {navigationOnly: true});
            isChangedFilter.current = false;
            window.addEventListener('resize', onClose);
            return () => window.removeEventListener('resize', onClose);
        }
        apiLocker.unlockSession(formSession);
        return noop;
    }, [onClose, isOpen, formSession]);

    if (!isOpen) {
        return null;
    }

    const anchorEl = getCellWrap();
    const elementRect = anchorEl.getBoundingClientRect();

    return (
        <Portal>
            <div className="reactComp" onClick={e => e.stopPropagation()}>
                <DropdownV2
                    openingSide={getDropdownDirections(elementRect, isRTLDir, 600)}
                    width="contentWidth"
                    focusOnOpen
                    isOpen={isOpen}
                    onClose={handleClickOutside}
                    style={dropdownStyle}
                    trigger={
                        <FilterIconTrigger
                            icon={PriorityIcon.filterFill}
                            title={column.Title}
                            width={elementRect.width}
                            isOpen={isOpen}
                            onClose={onClose}
                        />
                    }
                    isCloseKeyCallback={iSearchDropdownCloseKey}
                    eventTypes={['touchstart', 'mousedown']}
                    onKeyDown={handleDropdownKeyDown}
                >
                    <FilterDropdownContent
                        onClose={onClose}
                        column={column}
                        setFilterChange={onChangeFilter}
                    />
                </DropdownV2>
            </div>
        </Portal>
    );
};
